<script setup lang="ts">
import { computed, inject, ref, useId } from 'vue';
import { NxCheckbox } from './../Inputs';
import { LockClosedIcon, ReOrderDotsVerticalIcon } from '@blpde/nx-icons';
import { i18nInjectionKey } from '../internal';

export type ColumnVisibilityItem = {
  key: string;
  label: string;
  visible: boolean;
  /** Pinned left ("fixiert"); only rendered as a toggle with `showSticky`. */
  sticky?: boolean;
};

const items = defineModel<ColumnVisibilityItem[]>({ required: true });
const props = defineProps<{
  showSticky?: boolean;
  /** Grow inside a bounded dialog, keeping 18em as the initial list height. */
  fill?: boolean;
}>();

const i18nInjection = inject(i18nInjectionKey)!;

const uniqueId = useId();

const draggedIdx = ref<number | null>(null);
const dropTargetIdx = ref<number | null>(null);

const allVisible = computed(() => items.value.length > 0 && items.value.every((i) => i.visible));

function toggleAll() {
  const target = !allVisible.value;
  items.value = items.value.map((item) => ({ ...item, visible: target }));
}

function toggle(idx: number) {
  const next = [...items.value];
  next[idx] = { ...next[idx], visible: !next[idx].visible };
  items.value = next;
}

function toggleSticky(idx: number) {
  const next = [...items.value];
  next[idx] = { ...next[idx], sticky: !next[idx].sticky };
  items.value = next;
}

function onDragStart(event: DragEvent, idx: number) {
  draggedIdx.value = idx;
  dropTargetIdx.value = idx;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    // required in Firefox to actually start the drag
    event.dataTransfer.setData('text/plain', String(idx));
  }
}

function onDragOver(event: DragEvent, idx: number) {
  if (draggedIdx.value === null) return;
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
  const target = event.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  const isAfter = event.clientY - rect.top > rect.height / 2;
  const targetIdx = isAfter ? idx + 1 : idx;
  if (dropTargetIdx.value !== targetIdx) {
    dropTargetIdx.value = targetIdx;
  }
}

function onDrop() {
  const from = draggedIdx.value;
  const to = dropTargetIdx.value;
  if (from === null || to === null) {
    onDragEnd();
    return;
  }
  // account for the removal shifting indices
  const adjustedTo = to > from ? to - 1 : to;
  if (adjustedTo !== from) {
    const next = [...items.value];
    const [item] = next.splice(from, 1);
    next.splice(adjustedTo, 0, item);
    items.value = next;
  }
  onDragEnd();
}

function onDragEnd() {
  draggedIdx.value = null;
  dropTargetIdx.value = null;
}
</script>

<template>
  <div
    class="scroll-area"
    :class="{ 'is-fill': props.fill }"
    @dragover.prevent
    @drop.prevent="onDrop"
  >
    <div class="select-all-row">
      <NxCheckbox
        :id="`${uniqueId}-select-all`"
        name="select-all"
        :model-value="allVisible"
        @update:model-value="toggleAll"
      />
      <label :for="`${uniqueId}-select-all`">
        {{
          allVisible
            ? i18nInjection.t('common.dialogs.columns.deselect_all')
            : i18nInjection.t('common.dialogs.columns.select_all')
        }}
      </label>
    </div>
    <ol>
      <li
        v-for="({ key, label, visible, sticky }, idx) in items"
        :key="key"
        :class="[
          'column-row',
          {
            dragging: draggedIdx === idx,
            'drop-before': dropTargetIdx === idx && draggedIdx !== idx && draggedIdx !== idx - 1,
            'drop-after':
              dropTargetIdx === idx + 1 && idx === items.length - 1 && draggedIdx !== idx,
          },
        ]"
        draggable="true"
        @dragstart="(e) => onDragStart(e, idx)"
        @dragover="(e) => onDragOver(e, idx)"
        @dragend="onDragEnd"
      >
        <ReOrderDotsVerticalIcon class="grip nxt-icon-md" />
        <NxCheckbox
          :id="`${uniqueId}-${key}`"
          :name="key"
          :model-value="visible"
          @update:model-value="toggle(idx)"
        />
        <label :for="`${uniqueId}-${key}`">{{ label }}</label>
        <button
          v-if="props.showSticky"
          type="button"
          class="sticky-toggle"
          :class="{ active: sticky }"
          :aria-pressed="!!sticky"
          :aria-label="`${i18nInjection.t('common.dialogs.columns.sticky')}: ${label}`"
          :title="i18nInjection.t('common.dialogs.columns.sticky')"
          @click="toggleSticky(idx)"
        >
          <LockClosedIcon class="nxt-icon-md" :filled="!!sticky" />
        </button>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.scroll-area {
  max-height: 18em;
  overflow-y: auto;
  padding: 0.125em 0;
}
.scroll-area.is-fill {
  flex: 1 1 auto;
  height: 18em;
  min-height: 0;
  max-height: none;
}
.select-all-row {
  position: sticky;
  top: 0;
  z-index: var(--nxt-z-raised);
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: 0 var(--nxt-space-2xs) var(--nxt-space-2xs);
  margin-bottom: 0.125em;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  background-color: var(--nxt-surface-raised-bg);
  font-weight: var(--nxt-font-weight-semibold);
  user-select: none;
}
.select-all-row label {
  cursor: pointer;
  flex: 1;
}
ol {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}
.column-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: 0.125em var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  font-weight: var(--nxt-font-weight-medium);
  cursor: grab;
  user-select: none;
  position: relative;
  transition: background-color var(--nxt-motion-fast) ease;
}
.column-row:hover {
  background-color: var(--nxt-brand-subtle-bg);
}
.column-row:active {
  cursor: grabbing;
}
.column-row.dragging {
  opacity: 0.4;
}
.column-row.drop-before::before,
.column-row.drop-after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: calc(var(--nxt-size-unit) * 0.125);
  background-color: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-sm);
  pointer-events: none;
}
.column-row.drop-before::before {
  top: calc(-1 * var(--nxt-hairline));
}
.column-row.drop-after::after {
  bottom: calc(-1 * var(--nxt-hairline));
}
.grip {
  color: var(--nxt-fg-subtle);
  flex-shrink: 0;
  transition: color var(--nxt-motion-fast) ease;
}
.column-row:hover .grip {
  color: var(--nxt-fg-muted);
}
label {
  cursor: inherit;
  flex: 1;
}
.sticky-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: var(--nxt-space-2xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-subtle);
  cursor: pointer;
  transition: color var(--nxt-motion-fast) ease;
}
.sticky-toggle:hover,
.sticky-toggle:focus-visible {
  color: var(--nxt-fg-muted);
}
.sticky-toggle.active {
  color: var(--nxt-brand-bg);
}
</style>
